/* ==========================================================================
   ZETA 行動履歴収集 アクション連携
   ==========================================================================

   カートイン/カートアウト・お気に入り追加/削除を zdAction(zeta-tracking-tag.liquid が
   ロードする行動履歴収集タグ)へ連携する安全ラッパー。
   仕様書: docs/Zeta-Search/APIDocs/行動履歴収集タグ仕様書.md
   「2. カートイン・カートアウト/お気に入り追加・削除時に商品情報を連携する関数の呼び出し」

   呼び出し元:
   - カートイン: custom-product.js(PDP/カートページ) / theme.js ProductForm(quick-buy等の
     フォールバック) / custom-catalog-order.liquid(カタログ注文の一括・単品)
   - カートアウト/数量変更: 本ファイルが window.fetch をラップし、/cart/change.js(POST)を
     全ページで横取りして差分を送る(カートページ・ミニカート・カートドロワー共通)
   - お気に入り: multilikes:wishlist:changed イベント(custom-wishlist.js /
     custom-customer-wishlist-page.js が成功時に発火)を本ファイルが一元購読

   設計方針:
   - 公開APIはすべて Promise を返し、決して reject しない(計測失敗がUIを壊さない)
   - ページ遷移/リロード直前の呼び出し元は返却 Promise を await することで送信完了を保証できる
     (zdAction の ○○Async 関数を使用。ハング防止のためタイムアウト付き)
   - zdAction はタグの非同期ロード後に生成されるため、未定義時はキューに積んで出現を待つ。
     返却 Promise は出現後の送信完了(または待機上限)まで resolve しない。
     タグが現れないまま諦めた後は即 resolve し、遷移を毎回待たせない
   - 除外: 荷造送料ライン(properties._legacy_product_as_fee_title を持つ行。
     custom-product.js / custom-cart.js と同じキー)と、ギフトラッピング商品
     (window.zetaTrackingConfig.excludedHandles / zeta-search-config.liquid で定義)
   ========================================================================== */

(() => {
  'use strict';

  if (window.zetaTracking && window.zetaTracking._installed) return;

  const config = window.zetaTrackingConfig || {};

  const FEE_TITLE_KEY = '_legacy_product_as_fee_title'; // 荷造送料ラインのプロパティ(custom-cart.js と同じ)
  const SEND_TIMEOUT_MS = 3000;        // Async送信の待機上限(遷移・リロードを止めすぎない)
  const ZD_WAIT_TIMEOUT_MS = 3000;     // zdAction 未ロード時に呼び出し元を待たせる上限
  const FLUSH_INTERVAL_MS = 250;       // zdAction 出現待ちの再試行間隔
  const FLUSH_MAX_TRIES = 20;          // 再試行上限(超えたら破棄。タグブロック環境を想定)

  const excludedHandles = {};
  (Array.isArray(config.excludedHandles) ? config.excludedHandles : []).forEach((handle) => {
    if (handle) excludedHandles[String(handle)] = true;
  });

  const noop = () => {};
  const warn = (error) => console.warn('[zeta-tracking]', error);
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /* ------------------------------------------------------------
     送信コア
     ------------------------------------------------------------ */

  // zdAction 未ロード時の保留分。{ name, payload, resolve } を積み、出現後にまとめて送る。
  // resolve は送信完了時(または待機上限・諦め時)に呼び、呼び出し元の await を解放する
  const pendingQueue = [];
  let flushTimer = null;
  // FLUSH_MAX_TRIES まで待っても zdAction が現れなかった(タグブロック等)。以降は待たずに即 resolve
  let zdUnavailable = false;

  function startFlushTimer() {
    if (flushTimer) return;
    let triesLeft = FLUSH_MAX_TRIES;
    flushTimer = setInterval(() => {
      if (window.zdAction) {
        pendingQueue.splice(0).forEach((entry) => {
          dispatchToZd(entry.name, entry.payload).then(entry.resolve, entry.resolve);
        });
      }
      if (window.zdAction || --triesLeft <= 0) {
        clearInterval(flushTimer);
        flushTimer = null;
        if (!window.zdAction) zdUnavailable = true;
        pendingQueue.splice(0).forEach((entry) => entry.resolve());
      }
    }, FLUSH_INTERVAL_MS);
  }

  // zdAction 出現まで保留し、送信完了か ZD_WAIT_TIMEOUT_MS のどちらか早い方で resolve する。
  // 上限に達してもエントリはキューに残すので、その後タグが現れれば送信自体は行われる
  function enqueueUntilZd(name, payload) {
    return new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      pendingQueue.push({ name: name, payload: payload, resolve: settle });
      setTimeout(settle, ZD_WAIT_TIMEOUT_MS);
      startFlushTimer();
    });
  }

  // zdAction の関数を実行する。○○Async(Promise返却・rejectしない仕様)を優先し、
  // 念のためタイムアウトと catch で多重防御する
  function dispatchToZd(name, payload) {
    try {
      const zd = window.zdAction;
      if (!zd) return Promise.resolve();
      const asyncFn = zd[name + 'Async'];
      if (typeof asyncFn === 'function') {
        return Promise.race([asyncFn.call(zd, payload), delay(SEND_TIMEOUT_MS)]).catch(noop);
      }
      if (typeof zd[name] === 'function') zd[name](payload);
    } catch (error) {
      warn(error);
    }
    return Promise.resolve();
  }

  // 単一送信。item: { item_id(商品handle), sku, quantity }
  function send(name, item) {
    try {
      if (!item || !item.item_id) return Promise.resolve();
      if (excludedHandles[String(item.item_id)]) return Promise.resolve();
      const payload = {
        item_id: String(item.item_id),
        sku: item.sku == null ? '' : String(item.sku),
        quantity: parseInt(item.quantity, 10)
      };
      if (!(payload.quantity > 0)) return Promise.resolve();
      if (!window.zdAction) {
        if (zdUnavailable) return Promise.resolve();
        return enqueueUntilZd(name, payload);
      }
      return dispatchToZd(name, payload);
    } catch (error) {
      warn(error);
      return Promise.resolve();
    }
  }

  // 荷造送料ライン判定(properties に _legacy_product_as_fee_title があれば手数料)
  function isFeeProperties(properties) {
    return !!(properties && properties[FEE_TITLE_KEY] != null);
  }

  /* ------------------------------------------------------------
     カートイン(/cart/add.js 共通)
     ------------------------------------------------------------ */

  // requestItems: /cart/add.js に送ったリクエストの items([{ id, quantity, properties? }])
  // responseJson: /cart/add.js のレスポンス(handle / sku を含む)
  //
  // 数量は必ずリクエスト側の値を使う(仕様の「差分」。レスポンスの items[].quantity は
  // 既存ラインと合算された値になり得るため使わない)。handle / sku はレスポンスから
  // variant id 突合で取得する。荷造送料ラインは除外し、商品毎に addToCart を呼ぶ。
  function trackAddFromCartAddResponse(requestItems, responseJson) {
    try {
      const reqItems = Array.isArray(requestItems) ? requestItems : [];
      // /cart/add.js のレスポンス形状は2種類ある。JSONで items[] を送った場合(custom-product.js /
      // custom-catalog-order.liquid)は { items: [...] }、FormData で単品追加した場合
      // (theme.js の ProductForm)はラインアイテム単体が返る。
      // theme.js 自身も hasOwnProperty('items') で分岐しており、後者を空配列として扱うと
      // その経路のカートイン計測が一切飛ばないため、ここで単品形状を正規化する。
      const resItems = responseJson && Array.isArray(responseJson.items)
        ? responseJson.items
        : (responseJson ? [responseJson] : []);
      const sends = [];
      reqItems.forEach((reqItem) => {
        if (!reqItem || reqItem.id == null || reqItem.quantity == null) return;
        if (isFeeProperties(reqItem.properties)) return;
        const match = resItems.find((item) => item && String(item.id) === String(reqItem.id));
        if (!match || !match.handle) return;
        sends.push(send('addToCart', {
          item_id: match.handle,
          sku: match.sku || '',
          quantity: reqItem.quantity
        }));
      });
      if (sends.length === 0) return Promise.resolve();
      return Promise.all(sends).then(noop).catch(noop);
    } catch (error) {
      warn(error);
      return Promise.resolve();
    }
  }

  /* ------------------------------------------------------------
     カート数量変更・削除(/cart/change.js)
     ------------------------------------------------------------ */

  // change: { handle, sku, previousQuantity(変更前), nextQuantity(変更後) }
  // 差分が正なら addToCart、負なら removeFromCart を差分数量で送る(仕様の差分連携)
  function trackCartQuantityChange(change) {
    try {
      if (!change || !change.handle) return Promise.resolve();
      const previous = parseInt(change.previousQuantity, 10);
      const next = parseInt(change.nextQuantity, 10);
      if (isNaN(previous) || isNaN(next)) return Promise.resolve();
      const diff = next - previous;
      if (diff === 0) return Promise.resolve();
      return send(diff > 0 ? 'addToCart' : 'removeFromCart', {
        item_id: change.handle,
        sku: change.sku || '',
        quantity: Math.abs(diff)
      });
    } catch (error) {
      warn(error);
      return Promise.resolve();
    }
  }

  /* ------------------------------------------------------------
     /cart/change.js の横取り(全ページ共通)
     ------------------------------------------------------------ */

  // theme.js の LineItemQuantity(カートページ・ミニカート・カートドロワー)は
  // POST /cart/change.js に { line, id, quantity } を送る。イベント(cart:updated 等)には
  // 変更前数量・handle・sku が無いため、fetch をラップして変更前のカートと突き合わせる。
  //
  // - 本ファイルは custom-cart.js(カートページのみ)より後に読み込まれるため、このラッパーは
  //   custom-cart.js の手数料同期ラッパーの外側に乗る。custom-cart.js 内部の手数料 reconcile は
  //   ラップ前の fetch を使うのでここには届かず、二重計測にならない
  // - 直後に location.reload() する呼び出し元(カートページ)のため、送信完了を await してから返す
  // - 変更前カートの取得と本リクエストを直列化し、連続操作でも変更前数量がずれないようにする
  const nativeFetch = window.fetch.bind(window);
  let changeChain = Promise.resolve();

  function isCartChangeRequest(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
    const method = init && init.method ? String(init.method).toUpperCase() : 'GET';
    return method === 'POST' && /\/cart\/change\.js(?:[?#]|$)/.test(url) && !!init && typeof init.body === 'string';
  }

  function findCartLine(cart, payload) {
    const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
    if (payload.line != null) {
      const line = parseInt(payload.line, 10);
      if (line > 0 && items[line - 1]) return items[line - 1];
    }
    if (payload.id != null) {
      const id = String(payload.id);
      return items.find((item) => item && String(item.key) === id)
        || items.find((item) => item && String(item.variant_id) === id)
        || null;
    }
    return null;
  }

  async function handleCartChange(input, init) {
    let payload;
    try {
      payload = JSON.parse(init.body);
    } catch (error) {
      return nativeFetch(input, init);
    }
    if (!payload || payload.quantity == null || (payload.line == null && payload.id == null)) {
      return nativeFetch(input, init);
    }

    let targetItem = null;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartResponse = await nativeFetch(root + 'cart.js', { credentials: 'same-origin' });
      if (cartResponse.ok) targetItem = findCartLine(await cartResponse.json(), payload);
    } catch (error) {
      warn(error);
    }

    const response = await nativeFetch(input, init);
    if (response.ok && targetItem && targetItem.handle && !isFeeProperties(targetItem.properties)) {
      await trackCartQuantityChange({
        handle: targetItem.handle,
        sku: targetItem.sku,
        previousQuantity: targetItem.quantity,
        nextQuantity: parseInt(payload.quantity, 10)
      });
    }
    return response;
  }

  window.fetch = function (input, init) {
    if (!isCartChangeRequest(input, init)) return nativeFetch(input, init);
    const run = () => handleCartChange(input, init);
    const result = changeChain.then(run, run);
    changeChain = result.catch(noop);
    return result;
  };

  /* ------------------------------------------------------------
     お気に入り(multilikes:wishlist:changed 一元購読)
     ------------------------------------------------------------ */

  // お気に入り一覧ページ経路は MultiLikes API レスポンスに sku が無いため、
  // wishlistSkuResolution: 'fetch' のとき /products/{handle}.js を1回取得して解決する
  // (handle 単位でメモ化。'empty' のときは空skuで送信)
  const productJsonPromises = {};

  function fetchProductJson(handle) {
    if (!productJsonPromises[handle]) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productJsonPromises[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    }
    return productJsonPromises[handle];
  }

  function resolveSku(handle, variantId, sku) {
    if (sku) return Promise.resolve(String(sku));
    if ((config.wishlistSkuResolution || 'fetch') !== 'fetch') return Promise.resolve('');
    if (variantId == null || variantId === '') return Promise.resolve('');
    return fetchProductJson(handle)
      .then((product) => {
        if (!product || !Array.isArray(product.variants)) return '';
        const variant = product.variants.find((v) => v && String(v.id) === String(variantId));
        return (variant && variant.sku) || '';
      })
      .catch(() => '');
  }

  document.addEventListener('multilikes:wishlist:changed', (event) => {
    try {
      const detail = (event && event.detail) || {};
      if (detail.action !== 'add' && detail.action !== 'remove') return;
      const handle = detail.productHandle || '';
      if (!handle) return;

      // 対象バリアントの一覧化(removeAll の複数解除にも対応)
      let entries;
      if (Array.isArray(detail.variants) && detail.variants.length > 0) {
        entries = detail.variants; // [{ id, sku }]
      } else if (Array.isArray(detail.variantIds) && detail.variantIds.length > 0) {
        entries = detail.variantIds.map((id) => ({ id: id }));
      } else if (detail.variantId != null) {
        entries = [{ id: detail.variantId, sku: detail.variantSku }];
      } else {
        return;
      }

      const name = detail.action === 'add' ? 'addToFavorite' : 'removeFromFavorite';
      entries.forEach((entry) => {
        resolveSku(handle, entry && entry.id, entry && entry.sku).then((sku) => {
          send(name, { item_id: handle, sku: sku, quantity: 1 });
        });
      });
    } catch (error) {
      warn(error);
    }
  });

  /* ------------------------------------------------------------
     公開API
     ------------------------------------------------------------ */
  window.zetaTracking = {
    _installed: true,
    trackAddFromCartAddResponse: trackAddFromCartAddResponse,
    trackCartQuantityChange: trackCartQuantityChange,
    addToFavorite: (item) => send('addToFavorite', item),
    removeFromFavorite: (item) => send('removeFromFavorite', item)
  };
})();
